import {wellnessCoverage} from "@/utils/clubSnapshot";
import {
	summarizeTrainingAttendance,
	attendanceCoverage,
} from "@/utils/clubSnapshot";
import {useEffect, useState} from "react";
import moment from "moment";
import {useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsActivity,
	DsEmpty,
	DsTable,
	DsForm,
	DsKpi,
	DsKpiRow,
	DsLink,
	DsMissing,
	DsResultWarnings,
	DsSection,
	DsSkeleton,
	DsStatus,
} from "@/components/DsKit";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {RosterAPI} from "@/api/roster";
import {useClubCertificates, useClubSnapshot} from "@/utils/hooks";
import {
	DEPARTMENTS,
	getDepartment,
	playerName,
	scopeSnapshot,
	staffRoleName,
	matchDateLabel,
	matchCountdown,
} from "@/utils/clubSnapshot";
import {buildActivities} from "@/utils/clubControl";
import {getInitials} from "@/utils/functions";
import {summarizeMatrixRow} from "@/utils/rosterActivityMatrix";

const fmtNum = (v) =>
	v === null || v === undefined
		? "—"
		: Number(v).toLocaleString("it-IT", {maximumFractionDigits: 0});

// Modulo nel formato 4-3-3 a partire dai reparti del modulo salvato
const moduleLabel = (m) =>
	m?.name ||
	[
		m?.defenders,
		(m?.defensiveMidfielders || 0) + (m?.centralMidfielders || 0),
		m?.attackingMidfielders,
		m?.attackers,
	]
		.filter((n) => n)
		.join("-");

function DsTeamPage(props) {
	const {handleNotification} = props;
	const {id} = useParams();
	const navigate = useNavigate();
	const {
		data,
		loading,
		error: snapshotError,
	} = useClubSnapshot({handleNotification});
	const full = data?.full || data;
	const s = scopeSnapshot(full, id);
	const team = full?.teams.find((t) => t.id === id);
	const {data: certificates} = useClubCertificates({snapshot: s});

	const injuredIds = new Set((s?.activeInjuries || []).map((i) => i.playerId));
	const activities = team ? buildActivities(s, certificates) : [];
	const played = team ? [...team.played].reverse().slice(0, 3) : [];
	const rate = team?.organization.attendance.rate;
	const birthYears = [
		...new Set(
			(team?.players || [])
				.filter((p) => p.dateOfBirth)
				.map((p) => moment(p.dateOfBirth).year()),
		),
	].sort();
	const season = useSelector((state) => state?.season);

	// Dati delle tab "Informazioni" (matrice attività) e "Moduli" della scheda completa
	const [detail, setDetail] = useState(null);
	const [detailError, setDetailError] = useState(false);
	useEffect(() => {
		if (!id || !season?.id) return;
		let active = true;
		const today = moment().format("YYYY-MM-DD");
		setDetail(null);
		setDetailError(false);
		const load = async () => {
			try {
				const [roster, matchMatrix, trainingMatrix] = await Promise.all([
					RosterAPI.getById(id),
					RosterAPI.activityMatrix(id, {
						kind: "match",
						fromDate: moment(season.startDate).format("YYYY-MM-DD"),
						toDate: today,
					}),
					RosterAPI.activityMatrix(id, {
						kind: "training",
						fromDate: moment().subtract(29, "days").format("YYYY-MM-DD"),
						toDate: today,
					}),
				]);
				if (active) setDetail({roster, matchMatrix, trainingMatrix});
			} catch {
				if (active) {
					setDetail(null);
					setDetailError(true);
				}
			}
		};
		load();
		return () => {
			active = false;
		};
	}, [id, season?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	const usage = (detail?.matchMatrix?.players || [])
		.filter((p) => !p.guest)
		.map((p) => ({
			playerId: p.playerId,
			name: p.name,
			...summarizeMatrixRow(p, "match"),
		}))
		.map((u) => ({...u, played: u.attended}))
		.sort((a, b) => (b.minutes || 0) - (a.minutes || 0));
	const scorers = usage
		.filter((u) => u.goals)
		.sort((a, b) => b.goals - a.goals);
	const trainingAttendance = detail?.trainingMatrix
		? summarizeTrainingAttendance(detail.trainingMatrix)
		: null;
	const attendance =
		trainingAttendance?.rows.filter((row) => row.total > 0) || [];
	const toVerify = trainingAttendance?.unknown || 0;
	const defaultModule = (detail?.roster?.modules || []).find(
		(m) => m.isDefault,
	)?.module;
	const competitions = (detail?.roster?.competitionSeasons || [])
		.map((c) => c.competition?.name)
		.filter(Boolean);

	const missing = team
		? [
				!team.staff.length && {
					label: "Staff della squadra",
					owner: "Direzione",
				},
				detail &&
					!usage.some((u) => u.minutes) &&
					team.played.length > 0 && {
						label: "Minuti e marcatori",
						owner: "Team manager",
					},
				detail &&
					!attendance.length && {
						label: "Presenze agli allenamenti",
						owner: "Staff tecnico",
					},
				detail &&
					!defaultModule && {
						label: "Modulo di riferimento",
						owner: "Staff tecnico",
					},
				team.missingResults.length > 0 && {
					label: `${team.missingResults.length} risultati`,
					owner: "Staff tecnico",
				},
			].filter(Boolean)
		: [];

	return (
		<Page>
			<div className="ds">
				{snapshotError && (
					<DsEmpty>Dati DS non disponibili o accesso non consentito.</DsEmpty>
				)}
				{detailError && (
					<DsEmpty>
						Dati della rosa non disponibili o accesso non consentito.
					</DsEmpty>
				)}
				<DsSkeleton visible={loading && !data} />
				{data && !team && (
					<DsEmpty>Squadra non trovata per la stagione selezionata.</DsEmpty>
				)}
				{team && !detailError && (
					<>
						<DsResultWarnings teams={[team]} />
						<div className="ds-profile">
							<span className="ds-profile__avatar ds-profile__avatar--team">
								{getInitials(...team.name.split(" "))}
							</span>
							<div className="ds-profile__main">
								<h1 className="ds-profile__name">{team.name}</h1>
								<div className="ds-profile__sub">
									{[
										team.category !== team.name && team.category,
										`${team.players.length} giocatori`,
										team.avgAge !== null &&
											`età media ${team.avgAge.toFixed(1)}`,
										`${team.staff.length} nello staff`,
										defaultModule && `modulo ${moduleLabel(defaultModule)}`,
										competitions.length && competitions.join(", "),
									]
										.filter(Boolean)
										.join(" · ")}
								</div>
								<div className="ds-pills">
									<DsStatus
										status={team.injured.length ? "warn" : "ok"}
										label={
											team.injured.length
												? `${team.injured.length} infortunati`
												: "Rosa al completo"
										}
									/>
									{team.nextMatch && (
										<DsStatus
											status="none"
											label={`Prossima: ${team.nextMatch.homeClub} – ${team.nextMatch.awayClub}, ${matchCountdown(team.nextMatch)} · ${matchDateLabel(team.nextMatch, "ddd D MMM")}`}
										/>
									)}
								</div>
							</div>
							<div className="ds-profile__actions">
								<button
									className="ds-btn ds-btn--ghost"
									onClick={() => navigate("/manager/teams/" + team.id)}
								>
									<OpenInNewIcon fontSize="small" /> Scheda completa
								</button>
							</div>
						</div>

						<DsMissing items={missing} />

						<DsKpiRow>
							<DsKpi
								label="Senza infortunio attivo"
								value={`${team.available}/${team.players.length}`}
							/>
							<DsKpi
								label="Risultati"
								value={`${team.record.V}V ${team.record.N}N ${team.record.P}P`}
								sub={`${team.record.gf} gol fatti · ${team.record.ga} subiti`}
							/>
							<DsKpi label="Ultime gare" value={<DsForm form={team.form} />} />
							<DsKpi
								label="Presenze allenamenti"
								value={rate === null ? "—" : Math.round(rate * 100) + "%"}
								sub={
									"14 giorni · " +
									attendanceCoverage(team.organization.attendance)
								}
							/>
						</DsKpiRow>

						<DsSection
							title="Recupero e sforzo"
							hint="Ultimi7giorni inclusivi · giocatori appartenenti alla rosa, anche rilevazioni di altre sedute/gare autorizzate. Medie prima per giocatore, poi per rosa."
						>
							<DsKpiRow>
								<DsKpi
									label="Recupero · TQR"
									value={
										team.wellness.avgTQR == null
											? "—"
											: team.wellness.avgTQR.toLocaleString("it-IT", {
													minimumFractionDigits: 1,
													maximumFractionDigits: 1,
												})
									}
									sub={
										"su20 · " +
										wellnessCoverage(team.wellness.tqr, team.wellness.expected)
									}
								/>
								<DsKpi
									label="Sforzo · RPE"
									value={
										team.wellness.avgRPE == null
											? "—"
											: team.wellness.avgRPE.toLocaleString("it-IT", {
													minimumFractionDigits: 1,
													maximumFractionDigits: 1,
												})
									}
									sub={
										"su10 · " +
										wellnessCoverage(team.wellness.rpe, team.wellness.expected)
									}
								/>
							</DsKpiRow>
						</DsSection>
						<DsSection
							title="Rosa per reparto"
							hint="Clicca un giocatore per la sua sintesi. I minimi per reparto sono euristiche sperimentali DS, non requisiti di organico."
						>
							<div className="ds-roster">
								{DEPARTMENTS.map((d) => {
									const players = team.players
										.filter((p) => getDepartment(p) === d.id)
										.sort((a, b) =>
											(a.lastName || "").localeCompare(b.lastName || ""),
										);
									const short = players.length < d.min;
									return (
										<div key={d.id} className="ds-roster__col">
											<div className="ds-roster__head">
												<span>{d.label}</span>
												<span className={short ? "is-short" : ""}>
													{players.length}
													<small> / min {d.min}</small>
												</span>
											</div>
											{players.map((p) => {
												const contracts =
													full.contractsByPlayer.get(p.id) || [];
												const expiring = contracts.some(
													(c) =>
														c.daysLeft !== null &&
														c.daysLeft >= 0 &&
														c.daysLeft <= 180,
												);
												return (
													<div
														key={p.id}
														className="ds-roster__player"
														onClick={() => navigate("/ds/giocatore/" + p.id)}
													>
														<span className="ds-roster__name">
															{playerName(p)}
														</span>
														{p.dateOfBirth && (
															<span className="ds-roster__year">
																{moment(p.dateOfBirth).year()}
															</span>
														)}
														{injuredIds.has(p.id) && (
															<span className="ds-roster__tag is-injured">
																Inf.
															</span>
														)}
														{expiring && (
															<span className="ds-roster__tag is-expiring">
																Scad.
															</span>
														)}
													</div>
												);
											})}
											{!players.length && (
												<div className="ds-muted">Nessuno</div>
											)}
										</div>
									);
								})}
							</div>
						</DsSection>

						{birthYears.length > 0 && (
							<DsSection
								title="Annate per reparto"
								hint="Quanti giocatori per anno di nascita: dove la rosa è scoperta"
							>
								<DsTable
									columns={[
										{key: "label", label: "Reparto"},
										...birthYears.map((y) => ({
											key: String(y),
											label: String(y),
											align: "center",
											render: (r) => r.byYear[y] || "—",
										})),
									]}
									rows={DEPARTMENTS.map((d) => ({
										id: d.id,
										label: d.label,
										byYear: team.players
											.filter((p) => getDepartment(p) === d.id && p.dateOfBirth)
											.reduce((acc, p) => {
												const y = moment(p.dateOfBirth).year();
												acc[y] = (acc[y] || 0) + 1;
												return acc;
											}, {}),
									}))}
								/>
							</DsSection>
						)}

						<div className="ds-two-cols">
							<DsSection
								title="Chi gioca e chi segna"
								hint="Dalle distinte delle gare di stagione"
							>
								{usage.some((u) => u.minutes) ? (
									<div className="ds-facts">
										{usage.slice(0, 5).map((u) => (
											<div
												key={u.playerId}
												className="ds-facts__row is-clickable"
												onClick={() => navigate("/ds/giocatore/" + u.playerId)}
											>
												<span>{u.name}</span>
												<b>
													{[
														`${fmtNum(u.minutes)}'`,
														`${u.played} gare`,
														u.goals ? `${u.goals} gol` : null,
														u.assists ? `${u.assists} assist` : null,
													]
														.filter(Boolean)
														.join(" · ")}
												</b>
											</div>
										))}
									</div>
								) : (
									<DsEmpty>
										Minuti e marcatori non ancora inseriti nelle distinte.
									</DsEmpty>
								)}
								{scorers.length > 0 && (
									<div className="ds-muted ds-small mt-2">
										Marcatori:{" "}
										{scorers
											.slice(0, 5)
											.map((u) => `${u.name} ${u.goals}`)
											.join(" · ")}
									</div>
								)}
							</DsSection>

							<DsSection
								title="Presenze agli allenamenti"
								hint={
									"Ultimi 30 giorni fino a oggi · sole sedute concluse, ospiti inclusi · " +
									attendanceCoverage(trainingAttendance)
								}
							>
								{!detail ? (
									<DsEmpty>Caricamento presenze…</DsEmpty>
								) : attendance.length ? (
									<div className="ds-facts">
										{attendance.map((a) => (
											<div
												key={a.playerId}
												className="ds-facts__row is-clickable"
												onClick={() => navigate("/ds/giocatore/" + a.playerId)}
											>
												<span>
													{a.name}
													{a.guest ? " · Ospite" : ""}
												</span>
												<b>
													{a.attended}/{a.total} ·{" "}
													{Math.round((a.attended / a.total) * 100)}% ·{" "}
													{a.absent} assenti · {a.injured} infortunati ·{" "}
													{a.unknown} da verificare
												</b>
											</div>
										))}
									</div>
								) : (
									<DsEmpty>
										Nessuna partecipazione a sedute concluse negli ultimi 30
										giorni.
									</DsEmpty>
								)}
								{toVerify > 0 && (
									<div className="ds-muted ds-small mt-2">
										{toVerify} presenze da verificare negli allenamenti.
									</div>
								)}
							</DsSection>
						</div>

						<div className="ds-two-cols">
							<DsSection
								title="Cose aperte"
								action={<DsLink to={"/ds/attivita"}>Tutte le attività</DsLink>}
							>
								{activities.length ? (
									activities
										.slice(0, 6)
										.map((a) => (
											<DsActivity
												key={a.id}
												item={a}
												onOpen={a.path ? () => navigate(a.path) : undefined}
											/>
										))
								) : (
									<DsEmpty>Nessuna attività aperta per questa squadra.</DsEmpty>
								)}
							</DsSection>

							<DsSection title="Calendario">
								<div className="ds-list">
									{team.upcoming.slice(0, 3).map((m) => (
										<div
											key={m.id}
											className="ds-list__item is-clickable"
											onClick={() => navigate("/ds/gara/" + m.id)}
										>
											<div className="ds-date">
												<span className="ds-date__day">
													{moment(m.startDate).format("D")}
												</span>
												<span className="ds-date__month">
													{moment(m.startDate).format("MMM")}
												</span>
											</div>
											<div className="ds-list__main">
												<div className="ds-list__title">
													{m.homeClub} – {m.awayClub}
												</div>
												<div className="ds-list__sub">
													{m.competitionName || m.matchType || ""}
												</div>
											</div>
											<div className="ds-list__aside">
												{matchDateLabel(m, "ddd")}
											</div>
										</div>
									))}
									{played.map((m) => (
										<div
											key={m.id}
											className="ds-list__item is-clickable"
											onClick={() => navigate("/ds/gara/" + m.id)}
										>
											<DsForm form={[m.result]} />
											<div className="ds-list__main">
												<div className="ds-list__title">
													{m.home ? "vs" : "@"} {m.opponent}
												</div>
												<div className="ds-list__sub">
													{moment(m.startDate).format("ddd D MMM")}
												</div>
											</div>
											<div className="ds-list__aside">
												{m.gf} – {m.ga}
											</div>
										</div>
									))}
									{!team.upcoming.length && !played.length && (
										<DsEmpty>Nessuna gara in calendario.</DsEmpty>
									)}
								</div>
							</DsSection>
						</div>

						<DsSection
							title="Staff"
							action={<DsLink to="/ds/staff">Organigramma</DsLink>}
						>
							{team.staff.length ? (
								<div className="choice-chips">
									{team.staff.map((m) => (
										<span
											key={m.id}
											className="choice-chip choice-chip--static"
										>
											{playerName(m)}
											<span className="ds-muted">{staffRoleName(m)}</span>
										</span>
									))}
								</div>
							) : (
								<DsEmpty>Nessuno staff assegnato.</DsEmpty>
							)}
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsTeamPage;
